import CustomAutocomplete from "@/components/Form/InputFields/CustomAutocomplete";
import { AUDIENCE_TYPES } from "@/modules/Campaign/campaign.constant";
import { TName } from "@/types";
import { Box } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import { FieldValues, PathValue, useWatch } from "react-hook-form";
import { getSegmentIdsFromAudience } from "../../../targetingExpression.helpers";
import { SubModule, SubModuleCommonProps } from "../../../targetingExpression.interface";

const moduleName = "user";
const filterName = "custom_segment";
export default function CustomSegment<
  T extends FieldValues,
  U extends SubModule = "CUSTOM_SEGMENT"
>({
  control,
  errors,
  setValue,
  getValues,
  parentName,
  audiencesLoading,
  audiences,
  isEdit
}: CustomSegmentProps<T, U>) {

  const commonName = `${parentName}.${moduleName}.${filterName}.value`
  const audienceType = useWatch({
    name: `audienceType` as TName<T>,
    control
  })

  function onAudienceTypeChange() {
    setValue(`audienceIds` as TName<T>, [] as PathValue<T, TName<T>>);
    resetSegmentIds()
  }

  const { data } = audiences[audienceType ?? AUDIENCE_TYPES.Audience]

  const onAudienceChange = (ids: undefined | number[]) => {
    const segmentIdFormKey = `${commonName}.segmentIds` as TName<T>
    if (!ids || !ids?.length) {
      resetSegmentIds()
      return
    }
    const segmentIds = getSegmentIdsFromAudience({ ids, data })
    setValue(segmentIdFormKey, segmentIds as PathValue<T, TName<T>>, {
      shouldDirty: true
    })
  }

  const resetSegmentIds = () => {
    const segmentIdFormKey = `${commonName}.segmentIds` as TName<T>
    const segmentIds = getValues(segmentIdFormKey)
    if (segmentIds.length) setValue(segmentIdFormKey, [] as PathValue<T, TName<T>>, {
      shouldDirty: true
    })
    return
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "auto",
        padding: 2,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocomplete
          name={`audienceType` as TName<T>}
          label="Audience Type"
          itemList={audienceTypeList}
          control={control}
          error={errors?.audienceType?.message}
          onChange={onAudienceTypeChange}
          disableClearable
        />
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocomplete
          key={audienceType}
          name={`audienceIds` as TName<T>}
          label="Audience"
          itemList={audienceType ? audienceListMapping(data) : []}
          onChange={onAudienceChange}
          control={control}
          error={errors?.audienceIds?.message}
          loading={audiencesLoading}
          isMultiple
        />
      </Box>
    </Box>
  );
}
type AudienceKeys = typeof AUDIENCE_TYPES[keyof typeof AUDIENCE_TYPES]
type CustomSegmentProps<T extends FieldValues, U> = U extends "CUSTOM_SEGMENT"
  ? CustomSegment<T>
  : never;
interface CustomSegment<T extends FieldValues> extends SubModuleCommonProps<T> {
  audiencesLoading: boolean;
  audiences: {
    [key in AudienceKeys]: UseQueryResult<any, unknown>
  }
}


const audienceTypeList = [
  { label: "Account", value: AUDIENCE_TYPES.Audience },
  { label: "Contact", value: AUDIENCE_TYPES.EmailAudiences },
  { label: "Site Visitor", value: AUDIENCE_TYPES.RetargetAudience },
];

function audienceListMapping(data: any) {
  return data?.map((item: any) => ({
    label: item.segmentName,
    value: item.id,
  }));
}

